import api from "./api"
import { RegistrarLogErro } from "./clientefiel"

export interface CartelaItem {
    id: number
    nome: string
    telefone: string | null
    facebookId: string | null
    dataCriacao: number | null
    dataValidade: number | null
    dataUltimaAtualizacao: number | null
    status: string
    quantidadeSelos: number | null
    idCliente: number
}

export interface ResultadoCartelas {
    cartelas: CartelaItem[]
    total: number
    permiteResgateIncompleta: boolean
    isAdmin: boolean
}

export interface FiltroCartelas {
    parteNome: string
    email: string
    status: string
    inicio: string
    fim: string
}

export interface ErroApi {
    retornoErro: number
    mensagem: string
}

type RespostaRaw<T> = T & { codErro?: number; mensagem?: string }

function tratamentoResponse<T>(response: { data: RespostaRaw<T> }, chamada: string): T | ErroApi {
    const { data } = response
    if (data.codErro !== undefined && data.codErro > 0) {
        return { retornoErro: data.codErro, mensagem: data.mensagem ?? "" }
    }
    if (typeof data === "string" && data !== "") {
        return {
            retornoErro: 1,
            mensagem: `${chamada} - Erro inesperado, entre em contato com os responsáveis.`,
        }
    }
    return data
}

function erroParaRespostaRaw(error: unknown): RespostaRaw<never> {
    const axiosError = error as { message?: string; response?: { data?: unknown } }
    const data = axiosError.response?.data
    if (
        typeof data === "object" &&
        data !== null &&
        "codErro" in data &&
        (data as { codErro: number }).codErro > 0
    ) {
        const d = data as { codErro: number; mensagem?: string }
        return { codErro: d.codErro, mensagem: d.mensagem } as RespostaRaw<never>
    }
    if (axiosError.message === "Network Error") {
        return { codErro: 1, mensagem: "Verifique sua conexão!" } as RespostaRaw<never>
    }
    return { codErro: 1, mensagem: axiosError.message || "Erro desconhecido" } as RespostaRaw<never>
}

export const obterCartelas = async function (
    authorization: string,
    filtro: FiltroCartelas,
    pagina: number,
    tamanhoPagina: number
): Promise<ResultadoCartelas | ErroApi> {
    try {
        const payload: Record<string, unknown> = { pagina, tamanhoPagina }
        if (filtro.parteNome) payload.parteNome = filtro.parteNome
        if (filtro.email) payload.email = filtro.email
        if (filtro.status !== "") payload.status = Number(filtro.status)
        if (filtro.inicio) payload.inicio = filtro.inicio
        if (filtro.fim) payload.fim = filtro.fim

        const response = await api
            .post<RespostaRaw<ResultadoCartelas>>("cartela/ObterCartelas", payload, {
                headers: { "Content-Type": "application/json", Authorization: authorization },
            })
            .catch(function (error) {
                return { data: erroParaRespostaRaw(error) }
            })

        return tratamentoResponse<ResultadoCartelas>(response, "ObterCartelas")
    } catch (error) {
        RegistrarLogErro(error, "ObterCartelas", "")
        return { retornoErro: 1, mensagem: (error as Error).message }
    }
}

export const resgatarCartela = async function (
    authorization: string,
    id: number
): Promise<{ mensagem: string } | ErroApi> {
    try {
        const response = await api
            .post<RespostaRaw<{ mensagem: string }>>(`cartela/ResgatarCartela/${id}`, null, {
                headers: { "Content-Type": "application/json", Authorization: authorization },
            })
            .catch(function (error) {
                return { data: erroParaRespostaRaw(error) }
            })

        return tratamentoResponse<{ mensagem: string }>(response, "ResgatarCartela")
    } catch (error) {
        RegistrarLogErro(error, "ResgatarCartela", String(id))
        return { retornoErro: 1, mensagem: (error as Error).message }
    }
}

export const reativarCartela = async function (
    authorization: string,
    id: number
): Promise<{ mensagem: string } | ErroApi> {
    try {
        const response = await api
            .post<RespostaRaw<{ mensagem: string }>>(`cartela/ReativarCartela/${id}`, null, {
                headers: { "Content-Type": "application/json", Authorization: authorization },
            })
            .catch(function (error) {
                return { data: erroParaRespostaRaw(error) }
            })

        return tratamentoResponse<{ mensagem: string }>(response, "ReativarCartela")
    } catch (error) {
        RegistrarLogErro(error, "ReativarCartela", String(id))
        return { retornoErro: 1, mensagem: (error as Error).message }
    }
}

export const expirarCartelas = async function (
    authorization: string,
    status: number
): Promise<{ quantidade: number } | ErroApi> {
    try {
        const response = await api
            .post<RespostaRaw<{ quantidade: number }>>(
                "cartela/ExpirarCartelas",
                { status },
                {
                    headers: { "Content-Type": "application/json", Authorization: authorization },
                }
            )
            .catch(function (error) {
                return { data: erroParaRespostaRaw(error) }
            })

        return tratamentoResponse<{ quantidade: number }>(response, "ExpirarCartelas")
    } catch (error) {
        RegistrarLogErro(error, "ExpirarCartelas", String(status))
        return { retornoErro: 1, mensagem: (error as Error).message }
    }
}
